import * as Sentry from '@sentry/react'
import { apps } from '#/constants'
import * as Helpers from '#/helpers'

export const checkURL = (url) =>
  fetch('https://api.appsplus.co/broken', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url }),
  })
    .then((res) => res.json())
    .then((data) => {
      if (data && data.broken) return false
      return true
    })
    .catch(() => true)

export const encodeParams = (obj, incNull = false, omit = 'url') => {
  const params = Object.entries(obj)
    .filter(([k, v]) =>
      !incNull ? !omit?.includes(k) && v : !omit?.includes(k)
    )
    .map(([k, v]) => {
      if (Array.isArray(v)) {
        return v.map((x) => `${k}=${encodeURIComponent(x)}`).join('&')
      }
      return `${k}=${encodeURIComponent(v)}`
    })
    .join('&')
  return obj?.url?.includes('?') ? `&${params}` : `?${params}`
}

export const extractParams = (obj) => {
  const params = {}
  obj.searchParams.forEach((v, k) => {
    k = k.replace('amp;', '')
    if (params[k] !== undefined) {
      if (!Array.isArray(params[k])) {
        params[k] = [params[k]]
      }
      params[k].push(v)
    } else {
      params[k] = v
    }
  })
  return params
}

const normalizeUrl = (url) => {
  if (!url) return
  let newUrl = url
  const index = url.indexOf('://')
  if (index > -1 || url.startsWith('//')) newUrl = url.substr(index + 3)
  newUrl = `https://${newUrl}`

  return newUrl.replace(/\/+$/, '').replace(/\s+/g, ' ').trim()
}

export const extractLink = (link) => {
  const appExtract =
    Object.keys(apps).find((key) =>
      apps[key]?.domain?.some((x) => link?.includes(x))
        ? !apps[key]?.parent
        : false
    ) || null

  let linkExtract = null
  try {
    linkExtract = normalizeUrl(link)
    if (!link.includes('{{')) new URL(linkExtract) // smart value
  } catch (e) {
    linkExtract = null
  }

  // check if iframe
  const iframeURL = /(?:src|SRC)=(?:"|')(.+?)(?:"|'\s|>)/g.exec(
    link.replace(/%22/g, '"')
  )
  let iframeParams = {}
  if (iframeURL) {
    linkExtract = iframeURL[1].replace(/&amp;/g, '&')
    if (linkExtract.startsWith('//')) linkExtract = `https:${linkExtract}`
    if (!['powerbi.com', 'scribd.com'].some((x) => linkExtract?.includes(x))) {
      try {
        const url = new URL(linkExtract)
        linkExtract = url.href
        iframeParams = extractParams(url)
      } catch (e) {
        linkExtract = null
      }
    }
  }
  return { appExtract, linkExtract, iframeParams }
}

export const modifyAppKey = (appKey, pastedLink) => {
  if (!appKey && pastedLink?.split('.')?.pop() === 'pdf') return 'PDF'
  if (
    !appKey &&
    [
      'obj',
      '3ds',
      'stl',
      'ply',
      'gltf',
      'glb',
      'off',
      '3dm',
      'fbx',
      'dae',
      'wrl',
      '3mf',
      'ifc',
      'stp',
      'bim',
    ]?.includes(pastedLink?.split('.')?.pop())
  )
    return '3DVIEWER'
  if (pastedLink?.includes('pages.dev')) return 'CLOUDFLAREPAGES'
  if (pastedLink?.includes('dropbox.com')) return 'DROPBOX' // image override fix
  if (!appKey && pastedLink?.includes('.xls')) return 'MSEXCEL'
  if (!appKey && pastedLink?.includes('.ppt')) return 'MSPOWERPOINT'
  if (!appKey && pastedLink?.includes('.vsd')) return 'MSVISIO'
  if (!appKey && pastedLink?.includes('.doc')) return 'MSWORD'
  if (!appKey) return 'WEBSITE'
  return appKey
}

export const submitLink = async (
  pastedLink,
  tab,
  setTabs,
  setValidLink = () => {},
  setTempAppKey = () => {},
  setLoadingLink = () => {}
) => {
  if (!pastedLink) return

  const { id, config, embed } = tab
  const tabAppKey = config?.app?.toUpperCase()
  const { appExtract, linkExtract, iframeParams } = extractLink(pastedLink)

  let appKey = apps[tabAppKey]?.parent ? tabAppKey : appExtract || tabAppKey
  appKey = modifyAppKey(appKey, pastedLink)

  if (
    !linkExtract ||
    (apps[appKey]?.requires && !pastedLink?.includes(apps[appKey].requires))
  ) {
    setTempAppKey(appKey)
    setValidLink(false)
    setLoadingLink(false)
    return
  }

  if (!tabAppKey) {
    setTabs({
      type: 'edit',
      id,
      key: 'config',
      value: {
        ...config,
        title: apps[appKey].title.replace(/\s&.*$/, ''),
        app: appKey.toLowerCase(),
        icon: appKey.toLowerCase(),
        showIcon: true,
      },
    })
  }

  // replace smart values
  const fields = await Helpers.smartValueFields()
  const url = Helpers.smartValueURL(linkExtract, fields)

  // check is valid link
  const constructedLink = apps[appKey].embedURL({
    ...apps[appKey].embed,
    ...iframeParams,
    url,
  })

  Sentry.addBreadcrumb({
    message: 'link',
    data: {
      pasted: pastedLink,
      constructed: constructedLink,
    },
  })

  checkURL(constructedLink).then(async (status) => {
    if (!status && linkExtract) {
      // log URL for testing
      await fetch('https://api.appsplus.co/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ link: pastedLink }),
      })
      // set invalid link
      setValidLink(status)
      setLoadingLink(false)
      return
    }

    // embed settings cookie
    AP.cookie.read(`appsplusTabs:${appKey.toLowerCase()}`, (cookie) => {
      let defaultEmbed
      try {
        defaultEmbed = JSON.parse(cookie)
      } catch (e) {
        defaultEmbed = apps[appKey].embed
      }
      setTabs({
        type: 'edit',
        id,
        key: 'embed',
        value: {
          ...defaultEmbed,
          ...embed,
          ...iframeParams,
          url: linkExtract,
        },
      })
      setValidLink(status)
      setLoadingLink(false)
    })
  })
}
